import { useEffect, useRef, useState } from 'react';
import { Sprout, FlaskConical, Package, Truck, CheckCircle, Shield, Leaf, Award } from 'lucide-react';

export const ProcessInfographic = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.18 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      icon: Sprout,
      title: 'Paddy Procurement',
      description: 'Direct partnerships with trusted farmers in Haryana\'s fertile paddy regions',
      highlight: 'Farm-Direct',
      iconClass: 'icon-step-1'
    },
    {
      icon: FlaskConical,
      title: 'Quality Testing',
      description: 'Rigorous laboratory checks for pesticide residue, moisture & grain integrity',
      highlight: 'NABL Tested',
      iconClass: 'icon-step-2',
      decoration: 'flask'
    },
    {
      icon: Shield,
      title: 'Milling & Processing',
      description: 'Modern milling with Buhler & Satake equipment for premium grain output',
      highlight: 'Buhler & Satake',
      iconClass: 'icon-step-3'
    },
    {
      icon: CheckCircle,
      title: 'Sorting & Grading',
      description: 'Color sorting and length-based grading for export-grade consistency',
      highlight: 'Color Sorted'
    },
    {
      icon: Package,
      title: 'Packaging',
      description: 'Flexible formats — pouches, jute bags, non-woven & retail-ready cartons',
      highlight: 'Private Label',
      iconClass: 'icon-step-4'
    },
    {
      icon: Award,
      title: 'Export Documentation',
      description: 'APEDA, certificate of origin, phytosanitary & destination compliance',
      highlight: 'Compliance Ready'
    },
    {
      icon: Truck,
      title: 'Global Delivery',
      description: 'Container loading, fumigation & on-time shipping to 25+ countries',
      highlight: 'Worldwide',
      iconClass: 'icon-step-5',
      decoration: 'truck'
    }
  ];

  const certifications = [
    { name: 'APEDA', description: 'Agricultural Export Promotion', logo: '/certifications/apeda.png' },
    { name: 'FSSAI', description: 'Food Safety Standards', logo: '/certifications/fssai.png' },
    { name: 'USFDA', description: 'US Food & Drug Administration', logo: '/certifications/usfda.png' },
    { name: 'ISO 22000', description: 'Food Safety Management', logo: '/certifications/iso22000.png' },
    { name: 'HACCP', description: 'Hazard Analysis Critical Control', logo: '/certifications/haccp.png' }
  ];

  const renderIcon = (step, isMobile = false) => {
    const Icon = step.icon;
    const size = isMobile ? 'w-7 h-7' : 'w-8 h-8';

    if (step.decoration === 'flask') {
      return (
        <span className="flask-wrap inline-flex">
          <span className="flask-bubble b1" />
          <span className="flask-bubble b2" />
          <span className="flask-bubble b3" />
          <Icon className={`${size} text-navy group-hover:text-gold transition-colors duration-300 ${step.iconClass || ''}`} />
        </span>
      );
    }

    if (step.decoration === 'truck') {
      return (
        <span className="truck-wrapper inline-flex relative">
          <Icon className={`${size} text-navy group-hover:text-gold transition-colors duration-300`} />
          {/* Tiny spinning wheel accents */}
          <span
            className="truck-wheel absolute"
            style={{
              left: isMobile ? '7px' : '8px',
              bottom: isMobile ? '4px' : '5px',
              width: '5px',
              height: '5px',
              border: '1.5px solid #F5B041',
              borderRadius: '50%',
              borderLeftColor: 'transparent'
            }}
          />
          <span
            className="truck-wheel absolute"
            style={{
              right: isMobile ? '6px' : '7px',
              bottom: isMobile ? '4px' : '5px',
              width: '5px',
              height: '5px',
              border: '1.5px solid #F5B041',
              borderRadius: '50%',
              borderLeftColor: 'transparent'
            }}
          />
        </span>
      );
    }

    return (
      <Icon className={`${size} text-navy group-hover:text-gold transition-colors duration-300 ${step.iconClass || ''}`} />
    );
  };

  return (
    <section
      ref={sectionRef}
      data-testid="process-infographic"
      className={`py-24 md:py-32 leaf-pattern circle-watermark relative overflow-hidden ${inView ? 'process-in-view' : ''}`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #1A4D2E 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-24">
          <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
            Our Process
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mb-6">
            From Farm to Global Markets
          </h2>
          <div className="gold-line bg-gold mx-auto mb-6" />
          <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
            Every grain of Sawariya rice undergoes a meticulous journey ensuring premium quality and complete traceability
          </p>
        </div>

        {/* Process Steps - Desktop */}
        <div className="hidden lg:block relative mb-24">
          {/* Animated Connection Line */}
          <div className="absolute top-24 left-0 right-0 h-0.5 pointer-events-none">
            <div className="process-line h-full bg-gradient-to-r from-transparent via-gold to-transparent" />
            {/* Traveling grain */}
            <div className="process-grain" />
          </div>

          <div className="grid grid-cols-7 gap-4">
            {steps.map((step, index) => (
              <div
                key={index}
                className="process-step relative group"
                data-testid={`process-step-${index + 1}`}
              >
                {/* Icon Container */}
                <div className="relative mx-auto w-20 h-20 mb-6">
                  {/* Rotating dashed halo */}
                  <div
                    className="icon-halo-rotor absolute inset-[-6px] rounded-full pointer-events-none"
                    style={{
                      border: '1px dashed rgba(245, 176, 65, 0.45)'
                    }}
                  />
                  {/* Pulsing soft ring */}
                  <div className="icon-halo absolute inset-0 bg-gold/25 rounded-full" />
                  {/* White circle with icon */}
                  <div className="absolute inset-2 bg-white rounded-full shadow-lg flex items-center justify-center icon-float">
                    {renderIcon(step)}
                  </div>
                  {/* Step Number */}
                  <div className="step-badge absolute -top-2 -right-2 w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center font-heading text-sm font-bold shadow-md">
                    {index + 1}
                  </div>
                </div>

                {/* Content */}
                <div className="text-center">
                  <span className="highlight-shimmer inline-block px-3 py-1 text-gold text-xs tracking-widest uppercase mb-3 rounded">
                    {step.highlight}
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                    {step.title}
                  </h3>
                  <p className="font-body text-sm text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Process Steps - Mobile */}
        <div className="lg:hidden space-y-8 mb-16 relative">
          {/* Animated vertical line + traveling grain */}
          <div className="absolute left-8 top-8 bottom-8 w-0.5 pointer-events-none">
            <div className="process-line-v w-full h-full bg-gradient-to-b from-gold/60 via-gold/40 to-gold/10" />
            <div className="process-grain-v" />
          </div>

          {steps.map((step, index) => (
            <div
              key={index}
              className="process-step flex gap-6 items-start relative z-10"
              data-testid={`process-step-mobile-${index + 1}`}
            >
              {/* Icon */}
              <div className="relative flex-shrink-0">
                <div
                  className="icon-halo-rotor absolute inset-[-4px] rounded-full pointer-events-none"
                  style={{ border: '1px dashed rgba(245, 176, 65, 0.45)' }}
                />
                <div className="icon-halo absolute inset-0 bg-gold/20 rounded-full" />
                <div className="relative w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center icon-float">
                  {renderIcon(step, true)}
                </div>
                <div className="step-badge absolute -top-1 -right-1 w-6 h-6 bg-gold text-white rounded-full flex items-center justify-center font-heading text-xs font-bold shadow">
                  {index + 1}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 pt-2">
                <span className="highlight-shimmer inline-block px-2 py-0.5 text-gold text-xs tracking-widest uppercase mb-2 rounded">
                  {step.highlight}
                </span>
                <h3 className="font-heading text-lg font-semibold text-navy mb-1">
                  {step.title}
                </h3>
                <p className="font-body text-sm text-gray-600">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Key Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white p-8 border-l-4 border-gold shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
            <Leaf className="w-10 h-10 text-navy mb-4" />
            <h4 className="font-heading text-xl font-semibold text-navy mb-2">Pesticide Free</h4>
            <p className="font-body text-gray-600">100% compliant with EU pesticide regulations for safe, healthy rice</p>
          </div>
          <div className="bg-white p-8 border-l-4 border-gold shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
            <Package className="w-10 h-10 text-navy mb-4" />
            <h4 className="font-heading text-xl font-semibold text-navy mb-2">Private Labelling</h4>
            <p className="font-body text-gray-600">Custom branding and packaging solutions for your market needs</p>
          </div>
          <div className="bg-white p-8 border-l-4 border-gold shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
            <CheckCircle className="w-10 h-10 text-navy mb-4" />
            <h4 className="font-heading text-xl font-semibold text-navy mb-2">Quality Assured</h4>
            <p className="font-body text-gray-600">Every batch tested and certified before shipment</p>
          </div>
        </div>

        {/* Certifications */}
        <div className="bg-navy p-8 md:p-12 relative overflow-hidden" data-testid="certifications-section">
          {/* Grain texture */}
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
            backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
          }} />

          <div className="relative z-10 text-center mb-8">
            <Award className="w-12 h-12 text-gold mx-auto mb-4" />
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-2">
              Our Certifications
            </h3>
            <p className="font-body text-white/70">
              Internationally recognized quality and safety standards
            </p>
          </div>

          <div className="relative z-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {certifications.map((cert, index) => (
              <div
                key={index}
                data-testid={`certification-${cert.name.toLowerCase().replace(' ', '-')}`}
                className="bg-white rounded-lg p-5 text-center shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col items-center justify-between min-h-[180px]"
              >
                <div className="flex-1 flex items-center justify-center w-full mb-3">
                  <img
                    src={cert.logo}
                    alt={`${cert.name} certification logo`}
                    className="max-h-20 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h4 className="font-heading text-base font-bold text-navy mb-1">
                    {cert.name}
                  </h4>
                  <p className="font-body text-[11px] text-gray-600 leading-tight">
                    {cert.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
